// ../../node_modules/@mui/utils/esm/deepmerge.js
function isPlainObject(item) {
  return item !== null && typeof item === "object" && item.constructor === Object;
}
function deepClone(source) {
  if (!isPlainObject(source)) {
    return source;
  }
  const output = {};
  Object.keys(source).forEach((key) => {
    output[key] = deepClone(source[key]);
  });
  return output;
}
function deepmerge(target, source, options = {
  clone: true
}) {
  const output = options.clone ? {
    ...target
  } : target;
  if (isPlainObject(target) && isPlainObject(source)) {
    Object.keys(source).forEach((key) => {
      if (key === "__proto__") {
        return;
      }
      if (isPlainObject(source[key]) && key in target && isPlainObject(target[key])) {
        output[key] = deepmerge(target[key], source[key], options);
      } else if (options.clone) {
        output[key] = isPlainObject(source[key]) ? deepClone(source[key]) : source[key];
      } else {
        output[key] = source[key];
      }
    });
  }
  return output;
}

// src/theme/makeTheme.ts
import { createTheme, alpha } from "@mui/material";

// src/theme/customResponsiveFontSizes.ts
import { responsiveFontSizes } from "@mui/material";
function customResponsiveFontSizes(theme, options) {
  let newTheme = responsiveFontSizes(theme, options);
  newTheme.typography.body3 = {
    ...newTheme.typography.body1,
    ...newTheme.typography.body3
  };
  newTheme = responsiveFontSizes(newTheme, {
    ...options,
    variants: ["body3"],
    breakpoints: ["md"],
    factor: 4
  });
  return newTheme;
}

// src/theme/makeTheme.ts
var themeDefault = createTheme();
function muiCssBaselineStyleOverrides(themeBase) {
  return {
    a: {
      textDecoration: "none",
      color: themeBase.palette.link.main
    },
    h1: {
      ...themeBase.typography.h1
    },
    h2: {
      ...themeBase.typography.h2
    },
    h3: {
      ...themeBase.typography.h3
    },
    h4: {
      ...themeBase.typography.h4
    },
    h5: {
      ...themeBase.typography.h5
    },
    h6: {
      ...themeBase.typography.h6
    },
    "& h2, h3, h4, h5, h6": {
      margin: themeBase.spacing(3, 0, 1, 0)
    }
  };
}
function makeTheme(themeBaseOverrides, themeComponentsOverrides) {
  const themeBase = customResponsiveFontSizes(createTheme(deepmerge({
    palette: {
      primary: {
        main: "#77467E"
      },
      secondary: {
        main: "#E88B02"
      },
      error: {
        main: "#d04c3d",
        light: "#A20F0F"
        // TODO: Why is the light a more darker red??
      },
      success: {
        main: "#388e3c"
      },
      text: {
        primary: "#1B1A1E",
        disabled: "#C7C7C7"
      },
      link: themeDefault.palette.augmentColor({
        color: {
          main: "#337ab7"
        }
      }),
      hint: themeDefault.palette.augmentColor({
        color: {
          main: "#595B62"
        }
      }),
      cta: themeDefault.palette.augmentColor({
        color: {
          main: "#419745"
        }
      }),
      background: {
        default: "#FFFFFF",
        footer: "#171717",
        contrast: "#E3EAEF",
        icon: "#E7EFF5"
      },
      grey: {
        // TODO: not sure if we should overwrite this
        200: "#FAF8FA",
        300: "#C1C1C1",
        500: "#C7C7C7",
        800: "#4E4E4E",
        900: "#222426"
      },
      border: {
        dark: "#0000001f",
        grey: "#0000003f",
        light: "#DADADA",
        white: "#EFEFEF",
        purple: "#77467E",
        light_grey: "#6C757D"
      },
      shadow: {
        orange: "#e88b023d"
      },
      icon: {
        dropdown: "#aaaaaa"
      },
      neutral: {
        1: "#1B1A1E",
        2: "#4E4E4E",
        3: "#E9E9E9",
        4: "#FAF8FA",
        5: "#FFFFFF"
      }
    },
    gradients: {
      blue: "linear-gradient(180deg, #E3EAEF 0%, #FFFFFF 100%)",
      orange: "linear-gradient(30deg,rgba(232,139,2,0.1) 0%, #F9F9FA 50%)",
      purple: "linear-gradient(270deg, #8A5092 0%, #77467E 100%)",
      cta: "linear-gradient(270deg, #4CAF50 0%, #419745 100%)",
      combined: `radial-gradient(
                    100% 200% at 110% 100%,
                    ${alpha("#77467E", 0.075)} 0%,
                    ${alpha("#E3EAEF", 0)} 100%
                ), radial-gradient(
                    100% 100% at -20% 100%,
                    ${alpha("#E88B02", 0.05)} 0%,
                    ${alpha("#E3EAEF", 0)} 100%
                )`
    },
    shape: {
      borderRadius: 8
    },
    typography: {
      fontFamily: "PolySans, Helvetica, sans-serif",
      fontWeightMedium: 600,
      fontWeightBold: 600,
      button: {
        textTransform: "none"
      },
      h1: {
        fontSize: "2.5rem",
        fontWeight: 600
      },
      h2: {
        fontSize: "2rem",
        fontWeight: 600
      },
      h3: {
        fontSize: "1.75rem",
        fontWeight: 600
      },
      h4: {
        fontSize: "1.5rem",
        fontWeight: 600
      },
      h5: {
        fontSize: "1.25rem",
        fontWeight: 600
      },
      h6: {
        fontSize: "1.125rem",
        fontWeight: 600
      },
      // Added especially for blocks with less content, which still needs to stand out
      body3: {
        fontSize: "1.125rem"
      }
    },
    boxShadows: {
      subtle: "0 0 12px 0 rgba(0, 0, 0, 0.04)",
      subtleForWhiteBackground: "0 0 12px 0 rgba(0, 0, 0, 0.07)",
      highlight: "0 0 12px 0 rgba(0, 0, 0, 0.16)"
    }
  }, themeBaseOverrides)), {
    factor: 2,
    // This makes sure the max font sizes is really followed
    // Otherwise the size will be rounded to 4px grid material design
    disableAlign: true
  });
  return createTheme({
    ...themeBase,
    components: deepmerge({
      MuiCssBaseline: {
        styleOverrides: muiCssBaselineStyleOverrides(themeBase)
      },
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            body3: "p"
          }
        }
      },
      MuiContainer: {
        defaultProps: {
          maxWidth: "lg"
        }
      },
      MuiGrid: {
        defaultProps: {
          spacing: 2
        }
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontSize: "1rem"
          },
          body: {
            fontSize: "1rem"
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontSize: "1rem",
            padding: themeBase.spacing(1.25, 2),
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none"
            }
          },
          containedPrimary: {
            "&:not(:disabled)": {
              background: themeBase.gradients.purple
            }
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            "&:hover": {
              backgroundColor: "#EFF5F8"
            }
          }
        }
      }
    }, themeComponentsOverrides?.(themeBase))
  });
}
var makeTheme_default = makeTheme;
export {
  customResponsiveFontSizes,
  makeTheme_default as makeTheme,
  muiCssBaselineStyleOverrides
};
//# sourceMappingURL=index.js.map